// Suggest one set of core locations per zone, using the same seeded k-means++ design the
// Genius Map uses for soil sample points (lib/samplingDesign). Each zone is its own field:
// candidates are the stack cells inside it, covariates are the same design columns, and the
// count is fixed by acres — under 8 acres gets 6 cores, 8 acres and larger gets 8.
// Clearance is looser than the automap: 15 m from an inside corner, 10 m from a boundary.
import { api } from "../api";
import { metresPerDegree } from "./metres";
import {
  DEFAULT_EXCLUSION,
  EXCLUSION_KEY_LIST,
  EXCLUSION_KEYS,
  exclusionMask,
  type ExclusionCols,
  type ExclusionThresholds,
} from "./pointExclusion";
import {
  expandCategoricalFeatures,
  gridCandidates,
  hashSeed,
  pickDesignCovariates,
  pointInPolygons,
  polygonAreaM2,
  polygonsOf,
  suggestSamplingPoints,
  type LngLat,
} from "./samplingDesign";
import { stackCellData, stackColumns } from "./stackClient";

export const ZONE_CORE_ACRE_SPLIT = 8;
export const ZONE_CORE_COUNT_SMALL = 6;
export const ZONE_CORE_COUNT_LARGE = 8;

// Cores only. Genius Map / automap suggest keeps DEFAULT_EXCLUSION (80 m corner, 30 m boundary)
// and the 15 m outline buffer. Cores try to stay 15 m from an inside corner and 10 m from a boundary.
export const CORE_EXCLUSION: ExclusionThresholds = {
  ...DEFAULT_EXCLUSION,
  cornerM: 15,
  boundaryM: 10,
};
export const CORE_EDGE_BUFFER_M = 10;

export function coreCountForZone(acres: number): number {
  return acres < ZONE_CORE_ACRE_SPLIT
    ? ZONE_CORE_COUNT_SMALL
    : ZONE_CORE_COUNT_LARGE;
}

export function zoneAcres(feature: GeoJSON.Feature): number {
  const stamped = (feature.properties as { area_acres?: unknown } | null)
    ?.area_acres;
  if (typeof stamped === "number" && Number.isFinite(stamped) && stamped > 0)
    return stamped;
  return polygonAreaM2(feature.geometry) / 4046.86;
}

function exColsFrom(columns: Record<string, (number | null)[]>): ExclusionCols {
  return {
    waterway: columns[EXCLUSION_KEYS.waterway],
    corner: columns[EXCLUSION_KEYS.corner],
    boundary: columns[EXCLUSION_KEYS.boundary],
  };
}

export interface ZoneSuggestInput {
  zones: GeoJSON.FeatureCollection;
  /* * Stack cell centers for the whole field. Empty → a grid inside each zone. */
  candidates: LngLat[];
  features?: (number | null)[][];
  geoms?: GeoJSON.Geometry[];
  /* * Aligned to `candidates`. True drops the cell before clustering, same as Genius Map. */
  excluded?: boolean[];
  seed: number;
}

/* * One k-means++ design per zone polygon. Points on a shared edge stay with the first zone. */
export function suggestCoresInZones(a: ZoneSuggestInput): LngLat[] {
  const out: LngLat[] = [];
  const seen = new Set<string>();
  const features = a.zones.features ?? [];
  for (let z = 0; z < features.length; z++) {
    const polys = polygonsOf(features[z]!.geometry);
    if (!polys.length) continue;
    const count = coreCountForZone(zoneAcres(features[z]!));
    const inside: number[] = [];
    for (let i = 0; i < a.candidates.length; i++) {
      if (pointInPolygons(a.candidates[i]!, polys)) inside.push(i);
    }
    const clear = a.excluded ? inside.filter((i) => !a.excluded![i]) : inside;
    const use = clear.length ? clear : inside;
    let candidates: LngLat[];
    let cols: (number | null)[][] | undefined;
    let geoms: GeoJSON.Geometry[] | undefined;
    if (use.length) {
      candidates = use.map((i) => a.candidates[i]!);
      cols = a.features?.map((col) => use.map((i) => col[i] ?? null));
      geoms = a.geoms ? use.map((i) => a.geoms![i]!) : undefined;
    } else {
      candidates = gridCandidates(polys);
    }
    if (!candidates.length) continue;
    const pts = suggestSamplingPoints({
      polys,
      candidates,
      features: cols,
      geoms,
      count,
      seed: a.seed + z * 1000 + count,
      edgeBufferM: CORE_EDGE_BUFFER_M,
    });
    for (const p of pts) {
      const key = `${p.lng.toFixed(6)},${p.lat.toFixed(6)}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(p);
    }
  }
  return out;
}

/* * Genius Map's candidate pool (stack cells + design covariates + exclusion), then one design per zone. */
export async function suggestZoneCores(
  fieldId: string,
  zones: GeoJSON.FeatureCollection,
): Promise<LngLat[]> {
  let candidates: LngLat[] = [];
  let features: (number | null)[][] | undefined;
  let geoms: GeoJSON.Geometry[] | undefined;
  let excluded: boolean[] | undefined;
  try {
    const catalog = await api.fetchFieldCovariates(fieldId);
    const keys = pickDesignCovariates(catalog);
    const cell = await stackCellData(fieldId, keys);
    if (cell.centroids.length) {
      candidates = cell.centroids;
      geoms = cell.geoms as GeoJSON.Geometry[];
      if (keys.length)
        features = expandCategoricalFeatures(keys, cell.features);
    }
  } catch {
    /* no stack — each zone falls back to an in-zone grid, the same spatial fallback Genius Map uses */
  }
  if (geoms && candidates.length) {
    try {
      const { columns } = await stackColumns(fieldId, EXCLUSION_KEY_LIST);
      const { kx, ky } = metresPerDegree(candidates[0]?.lat ?? 40.7);
      excluded = exclusionMask(
        candidates,
        exColsFrom(columns),
        kx,
        ky,
        CORE_EXCLUSION,
      );
    } catch {
      /* exclusion covariates not built — place on every cell */
    }
  }
  return suggestCoresInZones({
    zones,
    candidates,
    features,
    geoms,
    excluded,
    seed: hashSeed(fieldId),
  });
}
